import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Text,
  Input,
  useToast,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { Table, TableContainer, Tbody, Th, Thead, Tr, Td, Select } from "@chakra-ui/react";
import { useContext, useState } from "react";
import { useCustomFormats } from "hooks/useCustomFormat";
import { useMutation } from "@tanstack/react-query";
import { LoanContext } from "providers/loan";
import { apiRequest, apiGetRequest } from "utils/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PercentField from "components/elements/PercentField";
import { DefaultPayments, useDefaultInterest } from "./useDefaultInterest";
import { useTenant } from "modules/tenant/providers/tenant-provider";

const DefaultInterestTable = ({ loan_id, showTitle }: { loan_id: string; showTitle?: boolean }) => {
  const { user } = useTenant();
  const toast = useToast();
  const [showAddRow, setShowAddRow] = useState(false);

  const {
    defaultPayments: defaultInterests,
    isLoading,
    isFetching,
    invalidate,
  } = useDefaultInterest({ loan_id });

  const onAddRow = async () => {
    await invalidate();
  };

  const handleDelete = async (id: string, loan_id: string) => {
    try {
      await apiRequest("/supabase/loans/payments/default_interest/delete", { id, loan_id }, "post");
      toast({
        title: "Default interest rate deleted",
        status: "success",
      });
      onAddRow();
    } catch (err) {
      toast({
        title: "Error deleting default interest rate",
        description: err,
        status: "error",
      });
    }
  };

  return (
    <Flex direction="column" gap={4}>
      <Flex justifyContent={showTitle ? "space-between" : "flex-end"} alignItems="center">
        {showTitle && (
          <Heading fontSize="lg" fontWeight="bold">
            Default Interest
          </Heading>
        )}
        {!showAddRow && user.type === "lender" && (
          <Button onClick={() => setShowAddRow(!showAddRow)} variant="white" size="sm">
            Add Default Interest
          </Button>
        )}
      </Flex>
      {showAddRow && (
        <DefaultInterestTableControls
          onAddRow={onAddRow}
          loan_id={loan_id}
          setShowAddRow={setShowAddRow}
        />
      )}
      <DefaultInterestTableDetail
        loan_id={loan_id}
        rows={defaultInterests}
        isLoading={isLoading || isFetching}
        onDelete={handleDelete}
      />
    </Flex>
  );
};

const DefaultInterestTableControls = ({
  onAddRow,
  loan_id,
  setShowAddRow,
}: {
  onAddRow: () => void;
  loan_id: string;
  setShowAddRow: (value: boolean) => void;
}) => {
  const { formatDate } = useCustomFormats();
  const { refetch: loadLoan } = useContext(LoanContext);
  const toast = useToast();

  const [newInterestRate, setNewInterestRate] = useState<{
    effective_from: string;
    effective_to: string | null;
    interest_rate: number;
  }>({
    effective_from: formatDate(new Date().toISOString()),
    effective_to: null,
    interest_rate: 0,
  });

  const onChange = (e: { target: { value: string; name: string } }) => {
    setNewInterestRate(oldRate => ({
      ...oldRate,
      [e.target.name]: e.target.value || null,
    }));
  };

  const { mutateAsync: insertAsync, isPending } = useMutation({
    mutationFn: async () => {
      return await apiRequest("/supabase/loans/payments/default_interest", {
        ...newInterestRate,
        loan_id,
      });
    },
  });

  const handleAddInterestRate = async () => {
    try {
      const currentPayments: DefaultPayments[] = await apiGetRequest(
        "/supabase/loans/payments/default_interest",
        { loan_id }
      );

      const hasOpenEndedEntry = currentPayments?.some(
        (payment: DefaultPayments) => payment.effective_to === null
      );

      if (newInterestRate.effective_to === null && hasOpenEndedEntry) {
        toast({
          title: "Validation Error",
          description: "Only one entry can have an open-ended End Date",
          status: "error",
        });
        return;
      }

      await insertAsync();
      setShowAddRow(false);

      if (onAddRow) {
        onAddRow();
      }
      toast({
        title: "Default interest rate saved",
        status: "success",
      });
      await loadLoan();
    } catch (err) {
      toast({
        title: "Error saving Default interest rate",
        description: err,
        status: "error",
      });
    }
  };

  return (
    <Flex flexDirection="column" gap={2}>
      <Flex gap={2}>
        <FormControl>
          <FormLabel>Start Date</FormLabel>
          <Input
            type="date"
            borderWidth={1}
            name="effective_from"
            value={newInterestRate.effective_from}
            onChange={event => onChange(event)}
            disabled={isPending}
          />
        </FormControl>
        <FormControl>
          <FormLabel>End Date</FormLabel>
          <Input
            type="date"
            borderWidth={1}
            name="effective_to"
            value={newInterestRate.effective_to || ""}
            onChange={event => onChange(event)}
            disabled={isPending}
          />
        </FormControl>
        <PercentField
          disabled={isPending}
          label="Interest rate"
          value={String(newInterestRate.interest_rate ?? 0)}
          onChange={v => {
            setNewInterestRate(oldRate => ({
              ...oldRate,
              interest_rate: Number(v),
            }));
          }}
        ></PercentField>

        <FormControl w="auto">
          <FormLabel>&nbsp;</FormLabel>
          <Button
            disabled={isPending}
            onClick={() => {
              setShowAddRow(false);
            }}
          >
            <FontAwesomeIcon icon={["far", "x"]} />
          </Button>
        </FormControl>
        <FormControl w="auto">
          <FormLabel>&nbsp;</FormLabel>
          <Button isLoading={isPending} onClick={handleAddInterestRate}>
            <FontAwesomeIcon icon={["far", "check"]} />
          </Button>
        </FormControl>
      </Flex>
    </Flex>
  );
};

const DefaultInterestTableDetail = ({
  rows,
  isLoading,
  onDelete,
  loan_id,
}: {
  rows?: DefaultPayments[];
  isLoading?: boolean;
  onDelete: (id: string, loan_id: string) => void;
  loan_id: string;
}) => {
  const { formatDate } = useCustomFormats();

  const noResults = isLoading || !rows?.length;

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Rate</Th>
            <Th>Effective From</Th>
            <Th>Effective To</Th>
            <Th w="45px"></Th>
          </Tr>
        </Thead>
        <Tbody>
          {noResults || isLoading ? (
            <Tr>
              <Td colSpan={7} textAlign="center">
                <Text p={4}>No default interest rates yet.</Text>
              </Td>
            </Tr>
          ) : (
            rows.map(row => (
              <Tr key={row.id}>
                <Td>{`${row.interest_rate}%`}</Td>
                <Td>{formatDate(row.effective_from ?? "")}</Td>
                <Td>{row.effective_to ? formatDate(row.effective_to) : "Current"}</Td>
                <Td>
                  <Flex justifyContent="center" alignItems="center" color="gray.200">
                    <Menu>
                      <MenuButton aria-label="Options" w="20px">
                        <FontAwesomeIcon icon={["far", "ellipsis-vertical"]} />
                      </MenuButton>
                      <MenuList>
                        <MenuItem
                          icon={<FontAwesomeIcon icon={["far", "trash-can"]} />}
                          onClick={() => onDelete(row.id, loan_id)}
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Flex>
                </Td>
              </Tr>
            ))
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

const DefaultInterestTab = ({ showTitle }: { showTitle?: boolean }) => {
  const { loan } = useContext(LoanContext);

  if (!loan?.id) return null;

  return <DefaultInterestTable loan_id={loan.id} showTitle={showTitle} />;
};

export default DefaultInterestTab;
